<script setup lang="ts">
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useSystemStore } from '@/stores/system';
import {
  RadNavbar,
  RadFooter,
  RadHeroSimple,
  RadCtaSection,
  RadCard,
  RadIcon
} from '@radsuite/design-system';

const router = useRouter();
const authStore = useAuthStore();
const systemStore = useSystemStore();

// Auth Handlers
async function signInWithProvider(provider: 'google' | 'discord' | 'slack' | 'github') {
  try {
    await authStore.signInWithProvider(provider);
    router.push('/dashboard');
  } catch (error) {
    console.error('Sign in failed:', error);
  }
}

async function signOut() {
  try {
    await authStore.signOut();
  } catch (error) {
    console.error('SignOut failed:', error);
  }
}

function contactSales() {
  window.location.href = 'mailto:contact@tacticstechnology.com';
}
</script>

<template>
  <div class="bg-white dark:bg-slate-950 min-h-screen transition-colors duration-300 font-sans">
    
    <RadNavbar
      :is-authenticated="authStore.isAuthenticated"
      :theme="systemStore.theme"
      @sign-in="signInWithProvider('google')"
      @get-started="signInWithProvider('google')"
      @go-to-dashboard="router.push('/dashboard')"
      @sign-out="signOut"
      @toggle-theme="systemStore.toggleTheme()"
    />
    
    <!-- About Us Hero Section -->
    <RadHeroSimple
      title="About"
      highlight-text="RADSuite"
      description="RADSuite is an early-access platform for supervised AI-agent collaboration and deployment workflows, building toward a complete operating system for the agentic era."
    />

    <!-- Mission Section -->
    <section class="py-20 px-4 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900/50 dark:to-slate-950">
      <div class="max-w-7xl mx-auto text-center">
        <h2 class="text-3xl md:text-4xl font-bold text-slate-900 dark:text-rad-white mb-6">
          Our Mission
        </h2>
        <p class="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-12">
          Building the infrastructure for a world powered by interconnected AI agents.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <RadCard hoverable class="text-center p-8 border border-slate-200 dark:border-white/20">
            <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500/20 to-red-500/20 flex items-center justify-center mx-auto mb-6">
              <RadIcon name="Rocket" :size="32" class="text-red-500" />
            </div>
            <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white mb-3">Build</h3>
            <p class="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">Empower teams to create powerful agentic applications with ease.</p>
          </RadCard>
          <RadCard hoverable class="text-center p-8 border border-slate-200 dark:border-white/20">
            <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-6">
              <RadIcon name="Settings" :size="32" class="text-blue-500" />
            </div>
            <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white mb-3">Deploy</h3>
            <p class="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">Provide infrastructure for agent deployment and management.</p>
          </RadCard>
          <RadCard hoverable class="text-center p-8 border border-slate-200 dark:border-white/20">
            <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <RadIcon name="Globe" :size="32" class="text-emerald-400" />
            </div>
            <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white mb-3">Scale</h3>
            <p class="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">Enable marketplace and collaboration capabilities as RADSuite grows.</p>
          </RadCard>
        </div>
      </div>
    </section>

    <!-- Company Info Section
    <section class="py-20 px-4 bg-white dark:bg-slate-950">
      <div class="max-w-7xl mx-auto">
        <h2 class="text-3xl md:text-4xl font-bold text-slate-900 dark:text-rad-white mb-12 text-center">
          Company Information
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
          <RadCard class="border border-slate-200 dark:border-white/20 shadow-lg">
            <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white mb-4">Legal Structure</h3>
            <div class="space-y-3 text-sm text-slate-600 dark:text-slate-400">
              <p><strong class="text-slate-900 dark:text-rad-white">Parent Company:</strong> Bioniclogic Inc</p>
              <p><strong class="text-slate-900 dark:text-rad-white">Operating Subsidiary:</strong> Tactics Technology LLC</p>
              <p><strong class="text-slate-900 dark:text-rad-white">Founded:</strong> 2017</p>
              <p><strong class="text-slate-900 dark:text-rad-white">Headquarters:</strong> United States</p>
            </div>
          </RadCard>
          <RadCard class="border border-slate-200 dark:border-white/20 shadow-lg">
            <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white mb-4">Business Focus</h3>
            <div class="space-y-3 text-sm text-slate-600 dark:text-slate-400">
              <p><strong class="text-slate-900 dark:text-rad-white">Industry:</strong> AI Infrastructure & Developer Tools</p>
              <p><strong class="text-slate-900 dark:text-rad-white">Focus:</strong> Containerized AI Agent Platform</p>
              <p><strong class="text-slate-900 dark:text-rad-white">Stage:</strong> Early Stage Technology</p>
              <p><strong class="text-slate-900 dark:text-rad-white">Vision:</strong> OS for the Agentic Era</p>
            </div>
          </RadCard>
        </div>
      </div>
    </section>
    -->

    <!-- Values Section -->
    <section class="py-20 px-4 bg-slate-50 dark:bg-slate-900/30">
      <div class="max-w-7xl mx-auto text-center">
        <h2 class="text-3xl md:text-4xl font-bold text-slate-900 dark:text-rad-white mb-12">
          Our Values
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
          <RadCard hoverable class="p-6 border-slate-200 dark:border-slate-800">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <RadIcon name="Shield" :size="24" class="text-red-500" />
              </div>
              <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white">Security First</h3>
            </div>
            <p class="text-sm text-slate-600 dark:text-slate-400">We design agent workflows around controlled environments, human approval, and visibility into activity.</p>
          </RadCard>
          <RadCard hoverable class="p-6 border-slate-200 dark:border-slate-800">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <RadIcon name="Heart" :size="24" class="text-blue-500" />
              </div>
              <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white">Developer Friendly</h3>
            </div>
            <p class="text-sm text-slate-600 dark:text-slate-400">We build tools that developers love to use, with intuitive APIs and comprehensive documentation.</p>
          </RadCard>
          <RadCard hoverable class="p-6 border-slate-200 dark:border-slate-800">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <RadIcon name="Users" :size="24" class="text-emerald-500" />
              </div>
              <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white">Open Ecosystem</h3>
            </div>
            <p class="text-sm text-slate-600 dark:text-slate-400">We believe in the power of open collaboration and community-driven innovation.</p>
          </RadCard>
          <RadCard hoverable class="p-6 border-slate-200 dark:border-slate-800">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <RadIcon name="TrendingUp" :size="24" class="text-amber-500" />
              </div>
              <h3 class="text-xl font-bold text-slate-900 dark:text-rad-white">Scale & Performance</h3>
            </div>
            <p class="text-sm text-slate-600 dark:text-slate-400">Deployment and resource-management capabilities are being validated through early customer workflows.</p>
          </RadCard>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <RadCtaSection
      title="Ready to Get Started?"
      subtitle="Build the future with RADSuite's agentic platform."
      :primary-button-text="authStore.isAuthenticated ? 'Go to Dashboard' : 'Request Early Access'"
      secondary-button-text="Contact Sales"
      @primary-click="authStore.isAuthenticated ? router.push('/dashboard') : signInWithProvider('google')"
      @secondary-click="contactSales"
    />

    <RadFooter />
  </div>
</template>